import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import DemoPlayer from "@/components/DemoPlayer";
import { HeartPulse, Mic, ShieldCheck, FileText, Stethoscope, Sparkles, UserRound, PlayCircle } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const [pendingRole, setPendingRole] = useState(localStorage.getItem("aura_pending_role") || "");
  const [showDemo, setShowDemo] = useState(false);

  const chooseRole = (r) => {
    setPendingRole(r);
    localStorage.setItem("aura_pending_role", r);
  };

  const signIn = () => {
    if (!pendingRole) { toast.error("Please choose how you'll use MDvia Care first"); return; }
    localStorage.setItem("aura_pending_role", pendingRole);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Mic, title: "Multilingual Voice Rx", desc: "Record consults in any global language, transcribed to clinical English." },
    { icon: Sparkles, title: "Smart Clinical Drafts", desc: "Differential diagnosis, tests & treatment drafted for you." },
    { icon: ShieldCheck, title: "Drug Safety Guard", desc: "Real-time allergy & interaction warnings before you prescribe." },
    { icon: FileText, title: "OPD Case Sheets", desc: "Printable OPD papers & certificates in PDF / Word." },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBF7]">
      <div className="flex-1 grid lg:grid-cols-2">
      {/* Left — brand & pitch */}
      <div className="relative overflow-hidden flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="absolute bottom-0 -right-10 w-80 h-80 rounded-full bg-blue-200/40 blur-3xl" />

        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E5AA8] flex items-center justify-center shadow-lg shadow-blue-900/20">
            <HeartPulse className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-[#123C73] leading-none">MDvia Care</h1>
            <p className="text-xs text-slate-500 mt-1">Design For Future</p>
          </div>
        </motion.div>

        <div className="relative py-10">
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Modern Medicine Meets Gentle Care for Better Living.
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-5 text-slate-600 max-w-md text-base leading-relaxed">
            Record the conversation, let MDvia Care listen. Get key symptoms, differential diagnosis,
            treatment & diagnostic tests — drafted instantly.
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
                className="rounded-2xl bg-white/70 backdrop-blur border border-amber-100/70 p-4 shadow-sm">
                <f.icon className="w-5 h-5 text-[#F59E0B]" />
                <h3 className="mt-2 font-semibold text-slate-800 text-sm">{f.title}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            data-testid="login-watch-demo" onClick={() => setShowDemo(true)}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E5AA8] hover:bg-[#123C73] text-white font-semibold pl-3 pr-5 py-2.5 shadow-lg shadow-blue-900/20 transition-colors">
            <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><PlayCircle className="w-5 h-5" /></span>
            Watch a quick demo
          </motion.button>
        </div>

        <p className="relative text-xs text-slate-400">Built for global outpatient clinic.</p>
      </div>

      {/* Right — sign in */}
      <div className="relative flex items-center justify-center p-8 bg-gradient-to-br from-[#EBF3FB] to-[#FFFBEB]">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
          className="w-full max-w-md rounded-3xl bg-white shadow-xl shadow-blue-900/10 border border-white p-8 sm:p-10">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E5AA8] to-[#123C73] flex items-center justify-center shadow-lg">
            <Stethoscope className="w-7 h-7 text-amber-300" />
          </div>
          <h3 className="font-display text-2xl font-bold text-slate-900 mt-6">Welcome</h3>
          <p className="text-slate-500 mt-2 text-sm">Tell us how you'll use MDvia Care, then sign in. Your records stay private to your account.</p>

          <p className="mt-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">I am a…</p>
          <div className="mt-2 grid grid-cols-3 gap-2.5">
            <button type="button" data-testid="login-role-doctor" onClick={() => chooseRole("doctor")}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${pendingRole === "doctor" ? "border-[#1E5AA8] bg-[#EBF3FB]" : "border-slate-200 hover:border-slate-300"}`}>
              <Stethoscope className={`w-5 h-5 ${pendingRole === "doctor" ? "text-[#1E5AA8]" : "text-slate-400"}`} />
              <p className="mt-1.5 text-sm font-semibold text-slate-800">Doctor</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">Full clinical access</p>
            </button>
            <button type="button" data-testid="login-role-assistant" onClick={() => chooseRole("assistant")}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${pendingRole === "assistant" ? "border-[#F59E0B] bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
              <UserRound className={`w-5 h-5 ${pendingRole === "assistant" ? "text-amber-600" : "text-slate-400"}`} />
              <p className="mt-1.5 text-sm font-semibold text-slate-800">Assistant</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">Manage appointments</p>
            </button>
            <button type="button" data-testid="login-role-patient" onClick={() => chooseRole("patient")}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${pendingRole === "patient" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
              <HeartPulse className={`w-5 h-5 ${pendingRole === "patient" ? "text-emerald-600" : "text-slate-400"}`} />
              <p className="mt-1.5 text-sm font-semibold text-slate-800">Patient</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">Consult doctors online</p>
            </button>
          </div>

          <button
            data-testid="google-signin-btn"
            onClick={signIn}
            disabled={!pendingRole}
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors py-3.5 font-semibold text-slate-700 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.7 34.6 27 35.6 24 35.6c-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.6C41.6 36.5 44 30.8 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
            Continue with Google
          </button>

          <p className="mt-6 text-xs text-slate-400 text-center leading-relaxed">
            By continuing you agree to use MDvia Care responsibly for clinical documentation.
            AI suggestions assist — the treating doctor remains fully responsible.
          </p>
        </motion.div>
      </div>
      </div>

      <Footer />
      <DemoPlayer open={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
